import { useEffect, useRef, useState } from 'react';
import {
  clearImpersonationSessionId,
  getImpersonationExpiresAt,
  getImpersonationSessionId,
  setImpersonationSession,
} from './impersonation';

interface ExchangeResponse {
  customToken: string;
  sessionId: string;
  expiresAt: string;
}

export interface UseImpersonationExchangeOptions {
  code: string | null;
  loginWithCustomToken: (token: string) => Promise<{ success: boolean; error?: string }>;
  /** Base URL for the API. Each app passes import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'. */
  apiBaseUrl: string;
  /** Where to hard-redirect after a successful sign-in. Defaults to '/dashboard'. */
  redirectPath?: string;
}

export type ImpersonationExchangeStatus = 'idle' | 'exchanging' | 'signing-in' | 'success' | 'error';

export interface UseImpersonationExchangeResult {
  status: ImpersonationExchangeStatus;
  errorMessage: string | null;
}

/**
 * Drives the full impersonation exchange flow for target apps:
 * POST /auth/impersonation/exchange → persist session → loginWithCustomToken → hard redirect.
 *
 * StrictMode-safe (uses a ref to ensure single execution in dev).
 * A hard redirect (window.location.replace) is used instead of a client-side
 * navigate so AuthContext can boot with auth.currentUser already populated.
 */
export function useImpersonationExchange({
  code,
  loginWithCustomToken,
  apiBaseUrl,
  redirectPath = '/dashboard',
}: UseImpersonationExchangeOptions): UseImpersonationExchangeResult {
  const [status, setStatus] = useState<ImpersonationExchangeStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (!code) return;
    if (ranRef.current) return;
    ranRef.current = true;

    const run = async () => {
      setStatus('exchanging');
      try {
        const response = await fetch(`${apiBaseUrl}/auth/impersonation/exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          setErrorMessage(`Impersonation exchange failed (${response.status}). ${text}`);
          setStatus('error');
          return;
        }

        const data = (await response.json()) as ExchangeResponse;
        setImpersonationSession(data.sessionId, data.expiresAt);

        setStatus('signing-in');
        const loginResult = await loginWithCustomToken(data.customToken);
        if (!loginResult.success) {
          setErrorMessage(loginResult.error || 'Failed to sign in with impersonation token.');
          setStatus('error');
          return;
        }

        setStatus('success');
        window.location.replace(redirectPath);
      } catch (err) {
        console.error('Impersonation exchange error:', err);
        setErrorMessage('Could not complete impersonation. Please close this tab and try again.');
        setStatus('error');
      }
    };

    run();
  }, [code, loginWithCustomToken, apiBaseUrl, redirectPath]);

  return { status, errorMessage };
}

/**
 * Ends an impersonation session server-side and clears client-side state.
 * Does not sign the user out — caller should do that (typically via AuthContext.logout).
 */
export async function endImpersonationSession(apiBaseUrl: string, idToken: string | null): Promise<void> {
  const sessionId = getImpersonationSessionId();
  if (idToken && sessionId) {
    try {
      await fetch(`${apiBaseUrl}/auth/impersonation/end`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'X-Impersonation-Session': sessionId,
          'Content-Type': 'application/json',
        },
      });
    } catch (err) {
      console.error('Failed to notify backend of impersonation end:', err);
    }
  }
  clearImpersonationSessionId();
}

/**
 * Asks the backend to extend the current impersonation session by 30 min.
 * Returns the new expiresAt on success, null on failure.
 */
export async function extendImpersonationSession(
  apiBaseUrl: string,
  idToken: string | null
): Promise<{ expiresAt: string } | null> {
  const sessionId = getImpersonationSessionId();
  if (!idToken || !sessionId) return null;
  try {
    const res = await fetch(`${apiBaseUrl}/auth/impersonation/extend`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'X-Impersonation-Session': sessionId,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { expiresAt?: string };
    if (!data.expiresAt) return null;
    setImpersonationSession(sessionId, data.expiresAt);
    return { expiresAt: data.expiresAt };
  } catch (err) {
    console.error('Failed to extend impersonation session:', err);
    return null;
  }
}

/**
 * Mutates document.title while an impersonation session is active so tab
 * switchers can distinguish real-user tabs from admin impersonation tabs.
 */
export function useImpersonationTabTitle(isImpersonating: boolean, label: string | null | undefined): void {
  useEffect(() => {
    if (!isImpersonating) return;
    const original = document.title;
    const marker = label ? `[IMPERSONATING ${label}] ` : '[IMPERSONATING] ';
    if (!original.startsWith(marker)) {
      document.title = `${marker}${original}`;
    }
    return () => {
      document.title = original;
    };
  }, [isImpersonating, label]);
}

/** Re-exports for convenience. */
export { getImpersonationExpiresAt, getImpersonationSessionId };
